import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Loader2, Heart, CheckCircle2 } from 'lucide-react';
import {
  WomensProgressBar,
  GoalsStep,
  PersonalInfoStep,
  SymptomsStep,
  LifestyleStep,
  MedicalHistoryStep,
  PreferencesStep,
  ReviewStep,
} from '@/components/intake/womens';
import ShippingBillingAddressStep from '@/components/intake/ShippingBillingAddressStep';
import { getIntakePrefillData } from '@/services/intakeService';
import { mapPrefillToWomensIntake } from '@/lib/intake/prefillMapper';
import PaymentStep from '@/components/intake/shared/PaymentStep';
import IntakeBookingStep from '@/components/intake/shared/IntakeBookingStep';
import { CalendarButtons } from '@/components/booking/CalendarButtons';
import { useGuidedIntakeStepFlow } from '@/hooks/useGuidedIntakeStepFlow';
import type { GuidedPaymentStatus } from '@/types/guidedOrder';

// Step indices for the HRT intake. Base flow ends at REVIEW_STEP; guided
// orders append PAYMENT_STEP for Stripe authorization before submit.
// Nimbus-fulfilled orders insert BOOKING_STEP between Review and Payment so
// that payment success bundles intake submission + appointment creation.
const REVIEW_STEP = 8;
const BOOKING_STEP = 9;
const PAYMENT_STEP = 10;

const STEP_TITLES = [
  'Goals',
  'Personal Info',
  'Symptoms',
  'Lifestyle',
  'Medical History',
  'Preferences',
  'Address',
  'Review'
];

interface WomensFormData {
  // Goals
  selectedGoals: string[];
  // Personal Info
  fullName: string;
  email: string;
  phone: string;
  state: string;
  age: number | null;
  dateOfBirth: string;
  heightFeet: number | null;
  heightInches: number | null;
  weight: number | null;
  smsConsent: boolean;
  // Symptoms
  symptoms: Record<string, number>;
  // Lifestyle
  activityLevel: string;
  sleepQuality: number;
  sleepHours: number | null;
  stressLevel: string;
  diet: string;
  caffeineCups: number | null;
  alcoholDrinks: number | null;
  smoking: string;
  // Medical History
  medications: string[];
  otherMedications: string;
  allergies: string[];
  previousHormoneTherapy: string;
  hormoneTherapyDetails: string;
  medicalConditions: string[];
  medicalConditionsOther: string;
  additionalMedicalHistory: string;
  // Preferences
  programRecommended: string;
  consultationMethod: string;
  refillFrequency: string;
  budget: string;
  additionalPreferences: string;
  // Shipping & Billing Address
  shippingAddressLine1: string;
  shippingAddressLine2: string;
  shippingCity: string;
  shippingState: string;
  shippingPostalCode: string;
  billingAddressLine1: string;
  billingAddressLine2: string;
  billingCity: string;
  billingState: string;
  billingPostalCode: string;
  billingSameAsShipping: boolean;
  // Consent
  consentAccurate: boolean;
  consentMonitoring: boolean;
  consentTerms: boolean;
}

const initialFormData: WomensFormData = {
  // Goals
  selectedGoals: [],
  // Personal Info
  fullName: '',
  email: '',
  phone: '',
  state: '',
  age: null,
  dateOfBirth: '',
  heightFeet: null,
  heightInches: null,
  weight: null,
  smsConsent: true,
  // Symptoms
  symptoms: {},
  // Lifestyle
  activityLevel: '',
  sleepQuality: 2, // Default to 'Fair'
  sleepHours: null,
  stressLevel: '',
  diet: '',
  caffeineCups: null,
  alcoholDrinks: null,
  smoking: '',
  // Medical History
  medications: [],
  otherMedications: '',
  allergies: [],
  previousHormoneTherapy: '',
  hormoneTherapyDetails: '',
  medicalConditions: [],
  medicalConditionsOther: '',
  additionalMedicalHistory: '',
  // Preferences
  programRecommended: '',
  consultationMethod: '',
  refillFrequency: '',
  budget: '',
  additionalPreferences: '',
  // Shipping & Billing Address
  shippingAddressLine1: '',
  shippingAddressLine2: '',
  shippingCity: '',
  shippingState: '',
  shippingPostalCode: '',
  billingAddressLine1: '',
  billingAddressLine2: '',
  billingCity: '',
  billingState: '',
  billingPostalCode: '',
  billingSameAsShipping: true,
  // Consent
  consentAccurate: false,
  consentMonitoring: false,
  consentTerms: false,
};

// Landing page for Women's Intake
const WomensIntakeLanding: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [isValidating, setIsValidating] = useState(true);
  const [isValid, setIsValid] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [patientInfo, setPatientInfo] = useState<{ firstName?: string; lastName?: string } | null>(null);
  const [clinicName, setClinicName] = useState<string>('');
  const brandTitle = clinicName || 'Nimbus Providers';
  const clinicBrandLine = clinicName ? 'Partnered with Nimbus Providers' : "Women's Health Intake";

  useEffect(() => {
    const validateToken = async () => {
      // For demo purposes, allow demo-token
      if (token === 'demo-womens' || token === 'demo-token') {
        setIsValid(true);
        setPatientInfo({ firstName: 'Demo', lastName: 'Patient' });
        setClinicName('Demo Clinic');
        setIsValidating(false);
        return;
      }

      try {
        const apiUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001/api';
        const response = await fetch(`${apiUrl}/intakes/validate/${token}`);
        const data = await response.json();
        
        if (data.success) {
          // Check if therapy category matches - redirect to correct form if needed
          const therapyCategory = data.data?.therapyCategory || data.therapyCategory;
          if (therapyCategory && therapyCategory !== 'hrt') {
            // Redirect to the correct intake form
            if (therapyCategory === 'trt') {
              navigate(`/patient/intake/mens/${token}`, { replace: true });
              return;
            } else {
              // General or other therapy types
              navigate(`/patient/intake/${token}`, { replace: true });
              return;
            }
          }
          
          setIsValid(true);
          setClinicName(data.data?.clinicName || data.data?.clinic?.name || '');
          // Try different possible response structures
          const patientData = data.intake?.patientInfo || data.patient || data.data?.patient;
          if (patientData) {
            // Handle different naming conventions
            setPatientInfo({
              firstName: patientData.firstName || patientData.first_name || patientData.name?.split(' ')[0],
              lastName: patientData.lastName || patientData.last_name || patientData.name?.split(' ')[1]
            });
          } else {
            setPatientInfo(null); // No patient info available
          }

          // Fetch prefill data after validation succeeds
          try {
            const prefillResult = await getIntakePrefillData(token);
            if (prefillResult.success && prefillResult.data) {
              const mappedPrefill = mapPrefillToWomensIntake(prefillResult.data);
              // Check if there's existing draft data in sessionStorage
              const savedData = sessionStorage.getItem(`womens-intake-${token}`);
              if (savedData) {
                // Merge prefill with draft (draft takes priority)
                const draftData = JSON.parse(savedData);
                setFormData({ ...mappedPrefill, ...draftData });
              } else {
                // No draft - use prefill as base
                setFormData(prev => ({ ...prev, ...mappedPrefill }));
              }
            }
          } catch (error) {
            console.error('Error fetching prefill data:', error);
            // Continue without prefill data
          }
        } else {
          setIsValid(false);
          setValidationError(data.message || data.error || 'This intake link is invalid or has expired.');
        }
      } catch (error) {
        console.error('Token validation error:', error);
        setIsValid(false);
        setValidationError('Unable to validate intake link. Please try again.');
      } finally {
        setIsValidating(false);
      }
    };

    validateToken();
  }, [token, navigate]);

  if (isValidating) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A0F1A]">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin mx-auto mb-4 text-[#4A8A9A]" />
          <p className="text-white/60">Validating your intake request...</p>
        </div>
      </div>
    );
  }

  if (!isValid) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A0F1A]">
        <div className="max-w-md text-center p-8 bg-[#1a2332] rounded-2xl shadow-lg border border-white/10">
          <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center bg-destructive/10">
            <span className="text-3xl">⚠️</span>
          </div>
          <h1 className="text-2xl font-bold mb-2 text-white font-montserrat">
            {validationError?.includes('cancelled') ? 'Link Cancelled' : 'Invalid or Expired Link'}
          </h1>
          <p className="mb-6 text-white/60">
            {validationError || 'This intake form link is invalid or has expired.'}
          </p>
          <p className="text-sm text-white/40">
            Please contact your healthcare provider for a new link.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0F1A]">
      {/* Header */}
      <header className="py-6 px-6 bg-[#0A0F1A] border-b border-white/10 shadow-sm">
        <div className="max-w-4xl mx-auto flex items-center gap-3">
          <Heart className="h-8 w-8 text-[#4A8A9A]" />
          <div>
            <h1 className="text-xl font-bold text-white font-montserrat">{brandTitle}</h1>
            <p className="text-sm text-white/90">{clinicBrandLine}</p>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6 bg-[#4A8A9A]/10 border border-[#4A8A9A]/20">
            <Heart className="h-4 w-4 text-[#4A8A9A]" />
            <span className="text-sm font-medium text-[#4A8A9A]">
              Women's Health Questionnaire
            </span>
          </div>
          
          <h2 className="text-4xl font-light tracking-tight mb-4 text-white font-montserrat">
            Welcome{patientInfo?.firstName ? `, ${patientInfo.firstName}` : ''}!
          </h2>
          
          <p className="text-lg max-w-2xl mx-auto text-white/60">
            This comprehensive questionnaire will help us understand your health goals and create a personalized hormone therapy plan tailored to your needs.
          </p>
        </div>

        {/* What to Expect */}
        <div className="p-8 rounded-2xl mb-8 bg-white/5 border-white/10">
          <h3 className="text-xl font-semibold mb-6 text-white font-montserrat">
            What to Expect
          </h3>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="flex flex-col items-center text-center">
              <div className="w-12 h-12 rounded-full flex items-center justify-center mb-3 bg-[#4A8A9A]/10">
                <span className="font-bold text-[#4A8A9A]">8</span>
              </div>
              <p className="font-medium text-white">Steps</p>
              <p className="text-sm text-white/60">Comprehensive assessment</p>
            </div>
            <div className="flex flex-col items-center text-center">
              <div className="w-12 h-12 rounded-full flex items-center justify-center mb-3 bg-[#4A8A9A]/10">
                <span className="font-bold text-[#4A8A9A]">15-25</span>
              </div>
              <p className="font-medium text-white">Minutes</p>
              <p className="text-sm text-white/60">Estimated completion time</p>
            </div>
            <div className="flex flex-col items-center text-center">
              <div className="w-12 h-12 rounded-full flex items-center justify-center mb-3 bg-[#4A8A9A]/10">
                <CheckCircle2 className="h-6 w-6 text-[#4A8A9A]" />
              </div>
              <p className="font-medium text-white">Auto-Save</p>
              <p className="text-sm text-white/60">Your progress is saved</p>
            </div>
          </div>
        </div>

        {/* Steps Preview */}
        <div className="p-8 rounded-2xl mb-8 bg-[#4A8A9A]/5 border border-[#4A8A9A]/20">
          <h3 className="text-lg font-semibold mb-4 text-white font-montserrat">
            Steps Overview
          </h3>
          <div className="grid md:grid-cols-2 gap-3">
            {STEP_TITLES.map((title, index) => (
              <div key={index} className="flex items-center gap-3">
                <span className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium bg-[#4A8A9A]/20 text-[#4A8A9A]">
                  {index + 1}
                </span>
                <span className="text-sm text-white/60">{title}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Start Button */}
        <div className="text-center">
          <button
            onClick={() => navigate(`/patient/intake/womens/${token}/form`)}
            className="inline-flex items-center gap-2 px-8 py-4 rounded-xl font-semibold transition-all hover:scale-105 bg-gradient-to-r from-[#4A8A9A] to-[#4A8A9A] text-white shadow-lg"
          >
            Begin Assessment
            <ArrowRight className="h-5 w-5" />
          </button>
          <p className="mt-4 text-sm text-white/60">
            Your information is securely encrypted and HIPAA compliant
          </p>
        </div>
      </main>
    </div>
  );
};

// Main Form Component
const WomensIntakeForm: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<WomensFormData>(initialFormData);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submissionError, setSubmissionError] = useState<string | null>(null);
  const [isValidating, setIsValidating] = useState(true);
  const [isCancelled, setIsCancelled] = useState(false);
  const [cancelledMessage, setCancelledMessage] = useState<string | null>(null);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [clinicName, setClinicName] = useState<string>('');
  const [providerName, setProviderName] = useState<string>('');
  // Payment is skipped when the hold was already placed / captured
  // (e.g. via storefront checkout) — see shouldSkip inside PaymentStep.
  const [isGuided, setIsGuided] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState<GuidedPaymentStatus | null>(null);
  const [isNimbusBookingEligible, setIsNimbusBookingEligible] = useState(false);
  const [bookedAppointment, setBookedAppointment] = useState<{
    id: string; start_time: string; end_time: string; duration: number;
    telehealthLink?: string | null;
  } | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<{
    slot_start: string; slot_end: string; timezone: string;
  } | null>(null);
  const showPayment = isGuided && paymentStatus !== 'hold_placed' && paymentStatus !== 'captured';
  const showBooking = isNimbusBookingEligible && showPayment;
  const { totalSteps, stepPosition } = useGuidedIntakeStepFlow({
    baseStepCount: REVIEW_STEP,
    bookingStep: BOOKING_STEP,
    paymentStep: PAYMENT_STEP,
    showBooking,
    showPayment,
    currentStep,
  });
  const brandTitle = clinicName || 'Nimbus Providers';
  const clinicBrandLine = clinicName ? 'Partnered with Nimbus Providers' : "Women's Health Intake";

  // Validate token on mount to check if cancelled and fetch prefill data
  useEffect(() => {
    const validateAndLoadData = async () => {
      if (!token || token.startsWith('demo')) {
        if (token?.startsWith('demo')) {
          setClinicName('Demo Clinic');
        }
        setIsValidating(false);
        setInitialLoadComplete(true);
        return;
      }

      try {
        // First check if there's existing draft data
        const savedData = sessionStorage.getItem(`womens-intake-${token}`);
        const hasDraft = savedData && savedData !== JSON.stringify(initialFormData);

        const apiUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001/api';
        const response = await fetch(`${apiUrl}/intakes/validate/${token}`);
        const data = await response.json();
        
        if (!data.success) {
          setIsCancelled(true);
          setCancelledMessage(data.message || data.error || 'This intake link is no longer valid.');
          // Clear saved form data
          sessionStorage.removeItem(`womens-intake-${token}`);
        } else {
          setClinicName(data.data?.clinicName || data.data?.clinic?.name || '');
          setProviderName(data.data?.providerName || '');
          setIsGuided(Boolean(data.data?.isGuided));
          setPaymentStatus((data.data?.paymentStatus ?? null) as GuidedPaymentStatus | null);
          setIsNimbusBookingEligible(Boolean(data.data?.isNimbusBookingEligible));
          // Fetch prefill data after validation succeeds
          try {
            const prefillResult = await getIntakePrefillData(token);
            if (prefillResult.success && prefillResult.data) {
              const mappedPrefill = mapPrefillToWomensIntake(prefillResult.data);
              
              if (hasDraft) {
                // Merge prefill with draft (draft takes priority)
                const draftData = JSON.parse(savedData);
                setFormData({ ...initialFormData, ...mappedPrefill, ...draftData });
              } else {
                // No draft - use prefill as base
                setFormData({ ...initialFormData, ...mappedPrefill });
              }
            } else if (hasDraft) {
              // No prefill but has draft - load draft
              setFormData(JSON.parse(savedData));
            }
          } catch (error) {
            console.error('Error fetching prefill data:', error);
            // Load draft if available
            if (hasDraft) {
              setFormData(JSON.parse(savedData));
            }
          }
        }
      } catch (error) {
        console.error('Token validation error:', error);
      } finally {
        setIsValidating(false);
        setInitialLoadComplete(true);
      }
    };

    validateAndLoadData();
  }, [token]);

  // Check if already submitted on mount
  useEffect(() => {
    const submittedStatus = sessionStorage.getItem(`womens-intake-submitted-${token}`);
    if (submittedStatus === 'true') {
      setIsSubmitted(true);
    }
  }, [token]);

  // Note: Draft loading is now handled in the validation effect above
  // to ensure proper merging with prefill data

  // Save form data to sessionStorage on change (only after initial load to avoid race condition)
  useEffect(() => {
    if (initialLoadComplete) {
      sessionStorage.setItem(`womens-intake-${token}`, JSON.stringify(formData));
    }
  }, [formData, token, initialLoadComplete]);

  const updateFormData = (field: string, value: unknown) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const canProceed = () => {
    switch (currentStep) {
      case 1:
        return formData.selectedGoals.length >= 1;
      case 2:
        return formData.fullName && formData.email && formData.state && formData.dateOfBirth;
      case 3:
        return true; // Symptoms are optional
      case 4:
        return true; // Lifestyle is optional
      case 5:
        return true; // Medical history details are optional
      case 6:
        return true; // Preferences are optional
      case 7: {
        const billingOk = Boolean(
          formData.billingAddressLine1?.trim() &&
          formData.billingCity?.trim() &&
          formData.billingState?.trim() &&
          formData.billingPostalCode?.trim() &&
          /^\d{5}(-\d{4})?$/.test(formData.billingPostalCode.trim())
        );
        if (!billingOk) return false;
        if (!formData.billingSameAsShipping) {
          return Boolean(
            formData.shippingAddressLine1?.trim() &&
            formData.shippingCity?.trim() &&
            formData.shippingState?.trim() &&
            formData.shippingPostalCode?.trim() &&
            /^\d{5}(-\d{4})?$/.test(formData.shippingPostalCode.trim())
          );
        }
        return true;
      }
      case 8:
        return formData.consentAccurate && formData.consentMonitoring && formData.consentTerms;
      default:
        return true;
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [currentStep]);

  const handleNext = () => {
    if (currentStep < REVIEW_STEP && canProceed()) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleEditStep = (stepNumber: number) => {
    setCurrentStep(stepNumber);
  };

  const handleSubmit = async (payment?: { paymentMethodId: string; setupIntentId: string }) => {
    if (!canProceed()) return;

    setIsSubmitting(true);
    setSubmissionError(null);

    try {
      const apiUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001/api';

      // Re-validate the intake status before submission (skip for demo tokens)
      if (!token?.startsWith('demo')) {
        const validateResponse = await fetch(`${apiUrl}/intakes/validate/${token}`);
        const validateData = await validateResponse.json();

        if (!validateData.success) {
          setIsCancelled(true);
          setCancelledMessage(validateData.message || 'This intake link is no longer valid.');
          sessionStorage.removeItem(`womens-intake-${token}`);
          setIsSubmitting(false);
          return;
        }
      }

      const response = await fetch(`${apiUrl}/intakes/submit/${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          responses: {
            ...formData,
          },
          scores: {},
          paymentMethodId: payment?.paymentMethodId,
          setupIntentId: payment?.setupIntentId,
          appointmentSlot: selectedSlot || undefined,
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        sessionStorage.setItem(`womens-intake-submitted-${token}`, 'true');
        sessionStorage.removeItem(`womens-intake-${token}`);
        if (data.data?.appointment) {
          setBookedAppointment(data.data.appointment);
        }
        setIsSubmitted(true);
      } else {
        // Check if intake was cancelled or expired
        if (data.message?.includes('cancelled') || data.message?.includes('expired')) {
          setIsCancelled(true);
          setCancelledMessage(data.message);
          sessionStorage.removeItem(`womens-intake-${token}`);
        } else {
          setSubmissionError(data.message || 'Failed to submit intake. Please try again.');
          console.error('Submission failed:', data);
        }
      }
    } catch (error) {
      console.error('Submission error:', error);
      setSubmissionError('Network error. Please check your connection and try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Show loading while validating
  if (isValidating) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A0F1A]">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin mx-auto mb-4 text-[#4A8A9A]" />
          <p className="text-white/60">Loading your intake form...</p>
        </div>
      </div>
    );
  }

  // Show cancelled message if intake was cancelled
  if (isCancelled) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A0F1A]">
        <div className="max-w-md text-center p-8 bg-[#1a2332] rounded-2xl shadow-lg border border-white/10">
          <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center bg-red-500/10">
            <span className="text-3xl">🚫</span>
          </div>
          <h1 className="text-2xl font-bold mb-2 text-white font-montserrat">
            {cancelledMessage?.includes('cancelled') ? 'Intake Cancelled' : 'Link No Longer Valid'}
          </h1>
          <p className="mb-4 text-white/60">
            {cancelledMessage || 'This intake form is no longer available.'}
          </p>
          <p className="text-sm text-white/40">
            Please contact your healthcare provider if you need to complete a new intake.
          </p>
        </div>
      </div>
    );
  }

  if (isSubmitted) {
    const providerLabel = providerName ? `Dr. ${providerName}` : 'your provider';
    const formattedDateTime = bookedAppointment
      ? `${new Date(bookedAppointment.start_time).toLocaleDateString('en-US', {
          weekday: 'long',
          month: 'long',
          day: 'numeric',
          year: 'numeric',
        })} at ${new Date(bookedAppointment.start_time).toLocaleTimeString('en-US', {
          hour: 'numeric',
          minute: '2-digit',
          hour12: true,
        })}`
      : null;
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A0F1A]">
        <div className="max-w-md text-center p-8 bg-[#1a2332] rounded-2xl shadow-lg border border-white/10">
          <div className="w-20 h-20 rounded-full mx-auto mb-6 flex items-center justify-center bg-[#4A8A9A]/10">
            <CheckCircle2 className="h-10 w-10 text-[#4A8A9A]" />
          </div>
          <h1 className="text-3xl font-bold mb-4 text-white font-montserrat">
            Thank You!
          </h1>
          <p className="mb-2 text-white/80">
            Thank you for completing your health questionnaire.
          </p>
          {formattedDateTime ? (
            <p className="mb-6 text-white/70">
              <span className="font-semibold text-white">Note:</span> Please join your video
              call on <span className="font-semibold text-white">{formattedDateTime}</span>.
              You'll receive a confirmation email with your video visit link.
            </p>
          ) : (
            <p className="mb-6 text-white/60">
              Your Women's Health assessment has been submitted successfully.
            </p>
          )}
          {bookedAppointment && (
            <div className="p-6 rounded-xl bg-[#4A8A9A]/10 border border-[#4A8A9A]/30 text-left">
              <h3 className="font-semibold mb-2 text-[#4A8A9A]">Your Appointment</h3>
              <p className="text-sm text-white/90 mb-4">{formattedDateTime}</p>
              <CalendarButtons
                appointment={{
                  start_time: bookedAppointment.start_time,
                  end_time: bookedAppointment.end_time,
                  title: `Telehealth Visit with ${providerLabel}`,
                  description: clinicBrandLine || undefined,
                  location:
                    bookedAppointment.telehealthLink || clinicBrandLine || undefined,
                }}
                practitionerName={providerLabel}
                clinicName={clinicBrandLine || undefined}
                telehealthLink={bookedAppointment.telehealthLink ?? undefined}
              />
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0F1A] intake-dark-form">
      {/* Header */}
      <header className="sticky top-0 z-50 py-4 px-6 bg-[#0A0F1A]/95 border-b border-white/10 shadow-sm backdrop-blur-sm">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <Heart className="h-6 w-6 text-[#4A8A9A]" />
              <div>
                <p className="font-semibold text-white/90">{brandTitle}</p>
                <p className="text-xs text-white/60">{clinicBrandLine}</p>
              </div>
            </div>
            <span className="text-sm text-white/60">
              Step {stepPosition} of {totalSteps}
            </span>
          </div>
          <WomensProgressBar
            currentStep={stepPosition}
            totalSteps={totalSteps}
            stepTitles={[
              ...STEP_TITLES,
              ...(showBooking ? ['Book Appointment'] : []),
              ...(showPayment ? ['Payment'] : []),
            ]}
          />
        </div>
      </header>

      {/* Form Content */}
      <main className="max-w-4xl mx-auto px-6 py-8">
        <div className="p-8 rounded-2xl bg-white/5 border-white/10">
          {currentStep === 1 && (
            <GoalsStep
              selectedGoals={formData.selectedGoals}
              onGoalsChange={(goals) => updateFormData('selectedGoals', goals)}
            />
          )}
          
          {currentStep === 2 && (
            <PersonalInfoStep
              formData={{
                fullName: formData.fullName,
                email: formData.email,
                phone: formData.phone,
                state: formData.state,
                age: formData.age,
                dateOfBirth: formData.dateOfBirth,
                heightFeet: formData.heightFeet,
                heightInches: formData.heightInches,
                weight: formData.weight,
                smsConsent: formData.smsConsent,
              }}
              updateFormData={updateFormData}
            />
          )}
          
          {currentStep === 3 && (
            <SymptomsStep
              symptoms={formData.symptoms}
              updateFormData={updateFormData}
            />
          )}
          
          {currentStep === 4 && (
            <LifestyleStep
              activityLevel={formData.activityLevel}
              sleepQuality={formData.sleepQuality}
              sleepHours={formData.sleepHours}
              stressLevel={formData.stressLevel}
              diet={formData.diet}
              caffeineCups={formData.caffeineCups}
              alcoholDrinks={formData.alcoholDrinks}
              smoking={formData.smoking}
              updateFormData={updateFormData}
            />
          )}
          
          {currentStep === 5 && (
            <MedicalHistoryStep
              medications={formData.medications}
              otherMedications={formData.otherMedications}
              allergies={formData.allergies}
              previousHormoneTherapy={formData.previousHormoneTherapy}
              hormoneTherapyDetails={formData.hormoneTherapyDetails}
              medicalConditions={formData.medicalConditions}
              medicalConditionsOther={formData.medicalConditionsOther}
              additionalMedicalHistory={formData.additionalMedicalHistory}
              updateFormData={updateFormData}
            />
          )}
          
          {currentStep === 6 && (
            <PreferencesStep
              programRecommended={formData.programRecommended}
              consultationMethod={formData.consultationMethod}
              refillFrequency={formData.refillFrequency}
              budget={formData.budget}
              additionalPreferences={formData.additionalPreferences}
              updateFormData={updateFormData}
            />
          )}

          {currentStep === 7 && (
            <ShippingBillingAddressStep
              shippingAddress={{
                addressLine1: formData.shippingAddressLine1,
                addressLine2: formData.shippingAddressLine2,
                city: formData.shippingCity,
                state: formData.shippingState,
                postalCode: formData.shippingPostalCode,
              }}
              billingAddress={{
                addressLine1: formData.billingAddressLine1,
                addressLine2: formData.billingAddressLine2,
                city: formData.billingCity,
                state: formData.billingState,
                postalCode: formData.billingPostalCode,
              }}
              billingSameAsShipping={formData.billingSameAsShipping}
              onShippingChange={(field, value) => {
                const fieldMap: Record<string, string> = {
                  addressLine1: 'shippingAddressLine1',
                  addressLine2: 'shippingAddressLine2',
                  city: 'shippingCity',
                  state: 'shippingState',
                  postalCode: 'shippingPostalCode',
                };
                updateFormData(fieldMap[field], value);
              }}
              onBillingChange={(field, value) => {
                const fieldMap: Record<string, string> = {
                  addressLine1: 'billingAddressLine1',
                  addressLine2: 'billingAddressLine2',
                  city: 'billingCity',
                  state: 'billingState',
                  postalCode: 'billingPostalCode',
                };
                updateFormData(fieldMap[field], value);
              }}
              onBillingSameToggle={(value) => updateFormData('billingSameAsShipping', value)}
              variant="dark"
            />
          )}

          {currentStep === REVIEW_STEP && (
            <>
              <ReviewStep
                formData={formData}
                onEditStep={handleEditStep}
                updateFormData={updateFormData}
              />
              {showPayment && (
                <p className="mt-4 text-xs" style={{ color: 'rgba(255, 255, 255, 0.5)' }}>
                  You&apos;ll authorize your card on the next step. No charge until your prescription is approved.
                </p>
              )}
            </>
          )}

          {currentStep === PAYMENT_STEP && showPayment && token && (
            <PaymentStep
              token={token}
              isGuided={isGuided}
              paymentStatus={paymentStatus}
              therapyLabel="HRT"
              isSubmitting={isSubmitting}
              onPaymentConfirmed={({ paymentMethodId, setupIntentId }) =>
                handleSubmit({ paymentMethodId, setupIntentId })
              }
              onSkip={() => handleSubmit()}
              onBack={() => setCurrentStep(showBooking ? BOOKING_STEP : REVIEW_STEP)}
            />
          )}

          {currentStep === BOOKING_STEP && showBooking && token && (
            <IntakeBookingStep
              token={token}
              initialSlot={selectedSlot}
              onSlotSelected={(slot) => {
                setSelectedSlot(slot);
                setCurrentStep(PAYMENT_STEP);
              }}
              onBack={() => setCurrentStep(REVIEW_STEP)}
            />
          )}
        </div>

        {/* Error Message */}
        {submissionError && (
          <div className="mt-6 p-4 rounded-lg bg-red-500/10 border border-red-500/20">
            <p className="text-red-400 text-sm">{submissionError}</p>
          </div>
        )}

        {/* Nav is hidden on Payment and Booking steps — they render their own controls. */}
        {currentStep !== PAYMENT_STEP && currentStep !== BOOKING_STEP && (
          <div className="flex items-center justify-between mt-8">
            <button
              onClick={handleBack}
              disabled={currentStep === 1}
              className="flex items-center gap-2 px-6 py-3 rounded-xl font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed bg-white/10 border border-white/20 text-white hover:bg-white/20"
            >
              <ArrowLeft className="h-5 w-5" />
              Back
            </button>

            {currentStep < REVIEW_STEP ? (
              <button
                onClick={handleNext}
                disabled={!canProceed()}
                className={`flex items-center gap-2 px-8 py-3 rounded-xl font-semibold transition-all disabled:cursor-not-allowed ${
                  canProceed()
                    ? 'bg-gradient-to-r from-[#4A8A9A] to-[#4A8A9A] text-white shadow-lg hover:scale-105'
                    : 'bg-slate-600 text-slate-400 border border-slate-500'
                }`}
              >
                Continue
                <ArrowRight className="h-5 w-5" />
              </button>
            ) : showBooking ? (
              <button
                onClick={() => setCurrentStep(BOOKING_STEP)}
                disabled={!canProceed()}
                className={`flex items-center gap-2 px-8 py-3 rounded-xl font-semibold transition-all disabled:cursor-not-allowed ${
                  canProceed()
                    ? 'bg-gradient-to-r from-[#4A8A9A] to-[#4A8A9A] text-white shadow-lg hover:scale-105'
                    : 'bg-slate-600 text-slate-400 border border-slate-500'
                }`}
              >
                Book Appointment
                <ArrowRight className="h-5 w-5" />
              </button>
            ) : showPayment ? (
              <button
                onClick={() => setCurrentStep(PAYMENT_STEP)}
                disabled={!canProceed()}
                className={`flex items-center gap-2 px-8 py-3 rounded-xl font-semibold transition-all disabled:cursor-not-allowed ${
                  canProceed()
                    ? 'bg-gradient-to-r from-[#4A8A9A] to-[#4A8A9A] text-white shadow-lg hover:scale-105'
                    : 'bg-slate-600 text-slate-400 border border-slate-500'
                }`}
              >
                Continue to payment
                <ArrowRight className="h-5 w-5" />
              </button>
            ) : (
              <button
                onClick={() => handleSubmit()}
                disabled={!canProceed() || isSubmitting}
                className={`flex items-center gap-2 px-8 py-3 rounded-xl font-semibold transition-all disabled:cursor-not-allowed ${
                  canProceed() && !isSubmitting
                    ? 'bg-gradient-to-r from-[#4A8A9A] to-[#4A8A9A] text-white shadow-lg hover:scale-105'
                    : 'bg-slate-600 text-slate-400 border border-slate-500'
                }`}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="h-5 w-5" />
                    Submit Assessment
                  </>
                )}
              </button>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

// Export both components
export { WomensIntakeLanding, WomensIntakeForm };
export default WomensIntakeLanding;
